import List from "list.js"
import nextUntil from "./utils/nextuntil"

interface FilterItem {
    name: string
    value: string
    text: string
}

interface FilterConfig {
    valueNames: {
        data: string[]
    }[]
    page: number
    listClass: string
}

function filter(config: Partial<FilterConfig>): void {
    const filterListElement = document.querySelector("[data-js-filter-list]")

    if (!filterListElement) return

    // We need to transform the markup a bit before initializing the script

    // Find startnode using closest (section)
    const startElement = document.querySelector("[data-js-filter-list]")?.closest(".section")
    // Find stopnodeselector using closest (section), and grab the id
    const stopAtId = document.querySelector(".filter-list__loadmore")?.closest(".section")?.id

    // No need to continue if either one of these doesn't exist
    if (!startElement || !stopAtId) return

    // This is the container where the filter items will be copied over to
    const filterItemsTarget = document.querySelector("[data-filter-target]")
    const placeholder = document.createDocumentFragment()

    // Find all elements between the filter header & footer
    const potentialFilterItems = nextUntil(startElement, `#${stopAtId}`)

    // Get all valid filter items from the previously selected nodes
    Array.from(potentialFilterItems).forEach((item) => placeholder.appendChild(item))
    const filterItems = placeholder.querySelectorAll("[data-filter]")

    // Apply grid classes to the filter items and append them to the filter list container
    Array.from(filterItems).forEach((item) => {
        item.classList.add("grid__col", "grid__col--md-6-of-12")
        filterItemsTarget.appendChild(item)
    })

    // Filter config
    const options = {
        valueNames: [{ data: ["filter"] }],
        page: 10,
        listClass: "filter-list__list",
        ...config,
    }

    const iconRemove = `
    <svg class="button__icon" width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 10L10 1" stroke="#242A52" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M1 1L10 10" stroke="#242A52" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`
    const filterList = new List(filterListElement as HTMLElement, options)
    const clearButton = document.querySelector(".filter-list__clear") as HTMLButtonElement
    const itemsSeenElement = document.querySelector(".filter-list__items-seen") as HTMLElement
    const itemsTotalElement = document.querySelector(".filter-list__items-total") as HTMLElement
    const progressElement = document.querySelector(".filter-list__progress") as HTMLProgressElement
    const progressTextElement = document.querySelector(".filter-list__progress-text") as HTMLElement
    const loadMoreButton = document.querySelector(".filter-list__load-more-button") as HTMLButtonElement
    const filterForm = document.querySelector(".filter-list__action-group--filter-options") as HTMLFormElement
    const filterListParentContainer = filterListElement.closest(".container") as HTMLElement

    // NOTE: Hacky solution for filter dropdowns potentially being overlapped by adjacent sections.
    if (filterListParentContainer) {
        filterListParentContainer.style.zIndex = "3"
    }

    let pageNumber = 1

    clearButton.addEventListener("click", resetList)
    loadMoreButton.addEventListener("click", loadMore)

    document.addEventListener("click", (e) => {
        const el = e.target as HTMLElement
        if (!el.classList.contains("filter-list__tag-btn")) return
        const input = document.querySelector(el.dataset.filterTarget) as HTMLInputElement
        input.checked = !input.checked
        filterForm.dispatchEvent(new Event("change", { cancelable: true }))
    })

    filterForm.addEventListener("change", (event) => {
        event.preventDefault()
        updateList()
    })

    function updateLoadMoreInfo(list: List) {
        const visibleItemsCount = list.visibleItems.length
        const matchingItemsCount = list.matchingItems.length
        const isDisabled = visibleItemsCount === matchingItemsCount
        const progress = matchingItemsCount !== 0 ? (visibleItemsCount / matchingItemsCount) * 100 : 0

        itemsSeenElement.innerText = visibleItemsCount.toString()
        itemsTotalElement.innerText = matchingItemsCount.toString()
        progressElement.value = progress
        progressElement.ariaValueNow = progress.toString()
        progressTextElement.innerText = progress.toString()
        loadMoreButton.disabled = isDisabled
    }

    function loadMore() {
        pageNumber += pageNumber
        filterList.show(0, pageNumber * options.page)
    }

    filterList.on("updated", (filterList) => {
        updateLoadMoreInfo(filterList)
        clearButton.hidden = document.querySelectorAll(".filter-list__checkbox:checked").length === 0
    })

    filterList.on("filterComplete", () => {
        pageNumber = 1
        filterList.show(0, pageNumber * options.page)

        const filters = Array.from<HTMLInputElement>(document.querySelectorAll(".filter-list__checkbox:checked")).map((checkBox) => {
            // NOTE: previous solution getting innerText of parentElement wasn't working (browser bug) in Safari (mobile/desktop).
            const filterTagText = checkBox.parentElement.querySelector("label").childNodes[0].textContent

            return { name: checkBox.name, value: checkBox.value, text: filterTagText }
        })

        const activeFilters: Array<FilterItem> = [...filters]
        renderTags(activeFilters)
    })

    function resetList() {
        filterList.filter()
        filterList.update()
        document.querySelectorAll<HTMLInputElement>(".filter-list__checkbox").forEach((checkBox) => (checkBox.checked = false))
        pageNumber = 1
        filterList.show(0, pageNumber * options.page)

        // Wipe any filter items from the filter container
        document.getElementById("filter-list__active-filters").innerHTML = ""
    }

    function renderTags(filters: Array<FilterItem>) {
        const filterTags = filters
            .map(
                (filter) =>
                    `<li class="filter-list__tag"><button type="button" class="button button--chip filter-list__tag-btn" data-filter-target="input[name='${filter.name}'][value='${filter.value}']">${filter.text} ${iconRemove}</button></li>`
            )
            .join("")
        document.getElementById("filter-list__active-filters").innerHTML = `<ul class="filter-list__tags h-ui-list h-m-zero">${filterTags}</ul>`
    }

    function updateList() {
        const groupedActiveFilters = Array.from(document.querySelectorAll(".filter-list__action-group--filter-options .dropdown"))
            .filter((filterGroup) => filterGroup.querySelectorAll(".filter-list__checkbox:checked").length > 0)
            .map((filterGroup) =>
                Array.from(filterGroup.querySelectorAll(".filter-list__checkbox:checked")).map((checkbox: HTMLInputElement) => checkbox.value)
            )

        filterList.filter((item) => {
            const filterItemValues = item.values() as Record<string, string>
            const filterValues = filterItemValues.filter.split(",")

            // Item must contain at least some filter from every filter group with an active filter value
            // Equal to OR withing a filter group, and AND between filter groups
            const itemShouldShow = groupedActiveFilters.every((filterGroup) => filterGroup.some((activeFilter) => filterValues.includes(activeFilter)))

            return itemShouldShow
        })
        filterList.update()
    }

    updateLoadMoreInfo(filterList)
}

export default filter
